/*
 * AZP-7008 — brand and model-number facet.
 *
 * Shopify cannot filter on appliance model numbers: a part fits ~1,200 machines and
 * `list.*` metafields stop at 128 entries. The facet is driven by static JSON on the
 * Shopify CDN instead — a catalogue fetched once, then one page of models per request.
 *
 * Brands come from the same index rather than Shopify's brand filter: the store may not
 * expose one, and the names come from a different source — `GE` and `General Electric`
 * are separate brands here and would not line up.
 *
 * The collection page is left exactly as the server rendered it until a model is picked,
 * so the crawlable, CDN-cached version of every collection stays intact.
 */
(function () {
  "use strict";

  var MODEL_KEY = "az_model";
  var SORT_KEY = "sort_by";
  var PART_KEY = "az_part";
  var AVAIL_KEY = "az_stock";
  var MIN_KEY = "az_min";
  var MAX_KEY = "az_max";
  var BRAND_KEY = "az_brand";
  /* AZP-7417 — which facet the shopper came in through. See `entryIsBrand`. */
  var ENTRY_KEY = "az_from";
  /* AZP-7078 — Part Type. Shopify product types carry commas and ampersands
   * ("Auger, Helix, Crusher"), and Chrome rewrites some of their escapes back on the way
   * out of `location.hash`, so the label cannot survive a round trip through the URL.
   * It travels as a slug instead, the way a brand does, and is resolved back against the
   * types the current query offers. A slug that matches nothing is dropped. */
  var TYPE_KEY = "az_type";
  var MODEL_STEP = 20;
  /* What this facet can order a filtered set by. The catalogue carries title and price;
   * The catalogue carries title, price and creation date, which covers six of the eight
   * orders the theme offers. "Featured" is a per-collection manual order, "Best selling"
   * needs sales figures, and "Most relevant" only exists inside a search query — none are
   * properties of a product, so those three stay disabled rather than pretending. */
  var SORTERS = {
    "title-ascending": function (a, b) { return a.t.localeCompare(b.t); },
    "title-descending": function (a, b) { return b.t.localeCompare(a.t); },
    "price-ascending": function (a, b) { return a.p - b.p; },
    "price-descending": function (a, b) { return b.p - a.p; },
    "created-ascending": function (a, b) { return a.d - b.d; },
    "created-descending": function (a, b) { return b.d - a.d; },
  };

  /* "Featured", "Best selling" and "Most relevant" are positions inside a set, not
   * properties of a product, so they are looked up in a captured order rather than
   * compared. `manual` is the merchant's own arrangement and barely moves; `best selling`
   * is only as fresh as the last index build; `relevance` is Shopify's own ranking for
   * the current query, captured from the handle list on a search page and therefore
   * always current. */
  var ORDER_SORTS = { manual: "m", "best-selling": "b", relevance: "r" };
  /* 250 handles per request, so this is 5,000 products — more than the store holds. A
   * query somehow larger than this would be scoped from a truncated set, which would
   * silently hide matches, so the facet steps aside instead. */
  var MAX_SCOPE_PAGES = 20;
  var RESULTS_PER_PAGE = 24;
  var CARD_SECTION = "model-result-card";
  var BRAND_STEP = 15;
  var TYPE_STEP = 10;
  /* Matches the cut `collection-category-facets.liquid` makes server-side; the two have
   * to agree or the fold would jump on load. */
  var CATEGORY_STEP = 15;


  /* Mirrors the theme's own `checkbox-facet` markup so this facet is indistinguishable
   * from the native ones. No `name` and no `form` attribute: these checkboxes must never
   * end up in `FacetsFilterForm`, which would send unknown params to Shopify. */
  var CHECK_SVG =
    '<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M17.5303 9.53033C17.8232 9.23744 17.8232 8.76256 17.5303 8.46967C17.2374 8.17678 16.7626 8.17678 16.4697 8.46967L17.5303 9.53033ZM9.99998 16L9.46965 16.5304C9.76255 16.8232 10.2374 16.8232 10.5303 16.5303L9.99998 16ZM7.53027 12.4697C7.23737 12.1768 6.7625 12.1768 6.46961 12.4697C6.17671 12.7626 6.17672 13.2374 6.46961 13.5303L7.53027 12.4697ZM16.4697 8.46967L9.46965 15.4697L10.5303 16.5303L17.5303 9.53033L16.4697 8.46967ZM6.46961 13.5303L9.46965 16.5304L10.5303 15.4697L7.53027 12.4697L6.46961 13.5303Z" /></svg>';

  function typeSlug(name) {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function optionHTML(kind, value, label, count, active) {
    var id = "az-filter-" + kind + "-" + value.replace(/[^A-Za-z0-9]+/g, "-");
    return (
      '<li class="facet-options-list-item">' +
      '<span class="checkbox-facet">' +
      '<label for="' + id + '" class="checkbox-facet-label">' +
      '<input type="checkbox" id="' + id + '" data-' + kind + '-value="' + value.replace(/"/g, "&quot;") + '"' +
      (active ? " checked" : "") + ' aria-label="' + label.replace(/"/g, "&quot;") + '">' +
      '<span class="checkbox-facet-check"></span>' +
      CHECK_SVG +
      '<span class="checkbox-facet-label-text">' + label + "</span>" +
      (count === null ? "" : '<span class="checkbox-facet-label-count">' + count + "</span>") +
      "</label></span></li>"
    );
  }

  function ModelFacet(root) {
    this.gridHTML = undefined;
    this.root = root;
    this.base = root.dataset.indexBase;
    this.version = root.dataset.indexVersion || "";
    // `/collections/all` is every product, so scoping by it would match nothing: the
    // catalogue lists real collection handles, and "all" is never one of them.
    var handle = root.dataset.collection || "";
    this.collection = handle === "all" ? "" : handle;
    // A search page has no collection to scope by; the query takes its place. `q` is read
    // from the URL rather than from Liquid so the exact string Shopify was given is the
    // one asked about, without a round trip through HTML escaping.
    this.isSearch = root.dataset.template === "search";
    this.query = this.isSearch ? this.param("q") : "";
    // `null` until a search page resolves its query: a map of the catalogue slots the
    // query matched. On a collection page it stays null and scoping runs off `c`.
    this.searchSlots = null;

    this.adopt(root);

    this.catalog = null;
    this.brands = {};
    this.brandSlugs = [];
    this.brandVisible = BRAND_STEP;
    this.models = []; // loaded so far, already collection-filtered
    this.modelVisible = MODEL_STEP;
    // AZP-7424 — the model search box: the keyword, trimmed and lower-cased, and a
    // counter that tells a page load started under one brand from the brand on screen
    // now, so a chain still in flight cannot push into the next brand's list.
    this.modelQuery = "";
    this.modelEpoch = 0;
    this.loadingAll = null;
    this.page = 0;
    this.pages = 1;
    this.brandSlug = null;
    // AZP-7303 — the brand a brand-collection page stands for, filled once the index
    // names the brands. Never a filter of this facet's own; see `resolvePageBrand`.
    this.pageBrand = null;
    this.brandCollections = null;
    this.model = null;
    this.results = [];
    this.resultPage = 1;
    this.originalCount = null;
    // Bumped on every render so cards from an abandoned selection cannot land in the
    // grid after a newer one started.
    this.renderToken = 0;
    this.sort = "";
    this.ready = false;
    this.orders = {};
    this.shards = {};
    this.manifest = null;
    this.handleSlot = {};
    this.stock = "";       // "in" | "out" | ""
    this.minPrice = null;
    this.maxPrice = null;
    // Part Type: the selected values, and the type of every catalogue slot the query
    // matched. Only a search page fills the map, so the facet is inert elsewhere.
    this.partTypes = [];
    this.slotType = {};
    this.typeNames = [];
    this.typeVisible = TYPE_STEP;

    this.bind();
    this.start();
  }

  /* Element lookup, kept out of the constructor so the markup and the wiring stay
   * separable — the facet is rendered by Liquid, this only fills it. */
  ModelFacet.prototype.adopt = function (root) {
    this.root = root;
    // Queried from the document, not from `root`: when `facets.js` rebuilds the sidebar it
    // moves the `.js-filter` blocks out of the `.model-facet` wrapper, so the wrapper stays
    // in the page while every control it used to hold is no longer inside it. Measured on
    // the dev store — one wrapper, one brand list, root still connected, and every
    // `root.querySelector` returning null.
    this.availabilityList = document.querySelector("[data-availability-list]");
    this.priceFrom = document.querySelector("[data-price-from]");
    this.priceTo = document.querySelector("[data-price-to]");
    // AZP-7303 — the range slider, its fill, and the button that commits the range.
    this.priceMinRange = document.querySelector('[data-price-range="min"]');
    this.priceMaxRange = document.querySelector('[data-price-range="max"]');
    this.priceFill = document.querySelector("[data-price-fill]");
    this.priceApply = document.querySelector("[data-price-apply]");
    this.priceEditor = document.querySelector("[data-price-editor]");
    this.priceChipList = document.querySelector("[data-price-chip-list]");
    this.brandList = document.querySelector("[data-brand-list]");
    this.brandMore = document.querySelector("[data-brand-more]");
    // AZP-7303 — Product and Component are rendered by Liquid as links and never
    // repainted here; only their counts, their empty branches and the brand they carry
    // are this facet's business.
    this.productList = document.querySelector("[data-product-list]");
    this.componentList = document.querySelector("[data-component-list]");
    this.typeList = document.querySelector("[data-type-list]");
    this.typeMore = document.querySelector("[data-type-more]");
    this.list = document.querySelector("[data-model-list]");
    this.more = document.querySelector("[data-model-more]");
    // AZP-7424 — the search box and the row that holds it. The theme re-renders the
    // sidebar with an empty field, so the keyword is read back off the element rather
    // than kept across the swap: what the shopper can see is what is filtering.
    this.searchRow = document.querySelector("[data-model-search-row]");
    this.search = document.querySelector("[data-model-search]");
    this.modelQuery = this.search ? this.search.value.trim().toLowerCase() : "";
    this.status = document.querySelector("[data-model-status]");
    this.clearButton = document.querySelector("[data-model-clear]");
    this.grid = document.getElementById("product-grid");
    // Captured once. Re-adopting after this facet has already painted would otherwise
    // record the filtered grid as the thing to restore, so clearing a filter would put
    // the filtered result back and look like nothing happened.
    if (this.gridHTML === undefined && this.grid) this.gridHTML = this.grid.innerHTML;
  };

  /* ---- data ---- */

  ModelFacet.prototype.url = function (name) {
    var url = this.base + name;
    return this.version ? url + "?v=" + encodeURIComponent(this.version) : url;
  };

  ModelFacet.prototype.fetchJSON = function (name) {
    return fetch(this.url(name))
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      });
  };

  /* The index carries stock as of the last build, which is enough to filter the whole
   * catalogue but goes stale between builds. Shopify publishes live stock for a whole
   * collection in one request, so the collection being browsed gets corrected from it —
   * measured at 169 KB compressed and 0.6s on the dev store, fetched after the page has
   * already painted.
   *
   * Only what this endpoint knows about is overwritten: it returns the first 250 products
   * of the collection, and the index still answers for everything outside that. */
  ModelFacet.prototype.refreshStock = function () {
    if (!this.collection) return Promise.resolve();
    var self = this;
    return fetch("/collections/" + this.collection + "/products.json?limit=250")
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (payload) {
        if (!payload || !payload.products) return;
        var changed = 0;
        payload.products.forEach(function (product) {
          var slot = self.handleSlot[product.handle];
          if (slot === undefined) return;
          var live = (product.variants || []).some(function (variant) {
            return variant.available;
          })
            ? 1
            : 0;
          if (self.catalog[slot].a !== live) {
            self.catalog[slot].a = live;
            changed += 1;
          }
        });
        if (!changed) return;
        // Counts and, if the shopper is already filtering by stock, the grid itself are
        // now wrong by exactly the products that moved.
        self.renderAvailability();
        if (self.stock) self.refilter();
      })
      .catch(function () {
        // The snapshot in the index remains; a failed refresh is not a failed page.
      });
  };

  /* Which products the query matched — all of them, in Shopify's own relevance order.
   *
   * Nothing on the storefront publishes this: the results page shows one page at a time
   * and `/search/suggest.json` stops at ten. `templates/search.handles.liquid` is an
   * alternate template that runs the same search and prints nothing but the handles, so
   * one request covers 250 products and the largest query the dev store answers — 365
   * for "heating" — costs two.
   *
   * Matching has to stay Shopify's. Matching catalogue titles instead recovers a
   * fraction: 12 products against 163 for "water filter", 9 against 182 for "door
   * switch", because the query also reaches description, tags, vendor and SKU. */
  ModelFacet.prototype.scopeURL = function (page) {
    return (
      "/search?q=" +
      encodeURIComponent(this.query) +
      "&type=product&view=handles&page=" +
      page
    );
  };

  ModelFacet.prototype.fetchScopePage = function (page) {
    return fetch(this.scopeURL(page))
      .then(function (response) {
        return response.ok ? response.text() : null;
      })
      .then(function (body) {
        // `layout none` still leaves the surrounding whitespace of the template, and the
        // response is served as HTML, so this is parsed rather than handed to `.json()`.
        if (!body) return null;
        try {
          return JSON.parse(body.trim());
        } catch (error) {
          return null;
        }
      })
      .catch(function () {
        return null;
      });
  };

  ModelFacet.prototype.fetchSearchHandles = function () {
    var self = this;
    if (!this.query) return Promise.resolve(null);

    return this.fetchScopePage(1).then(function (first) {
      if (!first || !first.h) return null;
      var pages = first.pages || 1;
      if (pages > MAX_SCOPE_PAGES) return null;
      if (pages < 2) return { h: first.h, y: first.y || [] };

      var rest = [];
      for (var page = 2; page <= pages; page++) rest.push(self.fetchScopePage(page));
      return Promise.all(rest).then(function (later) {
        var handles = first.h.slice();
        var types = (first.y || []).slice();
        for (var i = 0; i < later.length; i++) {
          // A hole in the middle would silently drop matches and leave the shopper
          // filtering a set that is not the one they searched for.
          if (!later[i] || !later[i].h) return null;
          handles = handles.concat(later[i].h);
          types = types.concat(later[i].y || []);
        }
        return { h: handles, y: types };
      });
    });
  };

  /* Handles become catalogue slots, and their order becomes the relevance ranking the
   * sort reads — the same mechanism "Featured" and "Best selling" use on a collection. */
  ModelFacet.prototype.applySearchScope = function (scope) {
    var self = this;
    var handles = scope.h;
    var types = scope.y || [];
    var slots = {};
    var order = [];
    handles.forEach(function (handle, position) {
      var slot = self.handleSlot[handle];
      if (slot === undefined || slots[slot]) return;
      slots[slot] = true;
      order.push(slot);
      // A product with no type has nothing to offer this facet; leaving it unmapped keeps
      // it out of the option list, and out of every set the facet filters.
      var type = types[position];
      if (type) self.slotType[slot] = type;
    });
    this.searchSlots = slots;
    this.orders.r = order;
    this.collectTypes();
  };

  /* The Part Type options: every type present in the matched set, A to Z.
   *
   * Product type is not in the index on the CDN and this shop's Search & Discovery offers
   * no Product type filter, so the values come from the same handle list that already
   * scopes the search — `templates/search.handles.liquid` prints them alongside. */
  ModelFacet.prototype.collectTypes = function () {
    var self = this;
    var seen = {};
    Object.keys(this.searchSlots || {}).forEach(function (slot) {
      var type = self.slotType[slot];
      if (type) seen[type] = true;
    });
    this.typeNames = Object.keys(seen).sort(function (a, b) {
      return a.localeCompare(b);
    });
  };

  /* The brands worth offering: those with at least one product in the set on screen.
   *
   * A collection is a fixed set, so `brands[slug].c` already answers this. A search
   * result set is not, so the answer comes from the brands written onto each catalogue
   * entry by the index builder. */
  ModelFacet.prototype.scopeBrands = function () {
    var self = this;
    var slugs;

    if (this.searchSlots) {
      var present = {};
      var known = false;
      Object.keys(this.searchSlots).forEach(function (slot) {
        var entry = self.catalog[slot];
        if (!entry || !entry.b) return;
        known = true;
        entry.b.forEach(function (slug) {
          if (self.brands[slug]) present[slug] = true;
        });
      });
      // An index built before brands were written onto the catalogue cannot answer this.
      // Offering every brand is wrong in a smaller way than offering none, and the model
      // list underneath is scoped either way.
      slugs = known ? Object.keys(present) : Object.keys(this.brands);
    } else {
      var collection = this.collection;
      slugs = Object.keys(this.brands).filter(function (slug) {
        var brand = self.brands[slug];
        return !collection || !brand.c || brand.c.indexOf(collection) !== -1;
      });
    }

    return slugs.sort(function (a, b) {
      return self.brands[a].b.localeCompare(self.brands[b].b);
    });
  };

  ModelFacet.prototype.start = function () {
    var self = this;
    this.setStatus("Loading…");

    Promise.all([
      this.fetchJSON("catalog.json"),
      this.fetchJSON("brands.json"),
      this.fetchJSON("orders.json"),
      // Runs alongside the index rather than after it: the query is answered by Shopify
      // and the catalogue by the CDN, so neither waits for the other.
      this.fetchSearchHandles(),
    ]).then(
      function (results) {
        self.orders = (results[2] || {})[self.collection] || {};
        if (!results[0] || !results[1]) {
          self.setStatus("Model list unavailable.");
          return;
        }
        self.catalog = results[0];
        self.brands = results[1];
        self.catalog.forEach(function (entry, index) {
          self.handleSlot[entry.h] = index;
        });
        if (self.query) {
          if (!results[3]) {
            // Without the matched set these facets would filter the whole catalogue and
            // quietly throw the query away, which is worse than not offering them.
            self.setStatus("Filters are unavailable for this search.");
            return;
          }
          self.applySearchScope(results[3]);
        }
        self.ready = true;
        self.pageBrand = self.resolvePageBrand();
        self.brandSlugs = self.scopeBrands();
        self.renderBrands();
        self.updateCategories();
        self.renderTypes();
        self.setStatus("Pick a brand to see the models it covers.");
        self.restore();
        // After restore, never before: correcting stock can re-run the filter, and doing
        // that while the URL state is still being applied would race it.
        self.refreshStock();
      }
    );
  };

  /* A model earns a place only if one of its parts sits in the collection being
   * browsed; otherwise picking it would land on an empty grid. */
  /* Every facet narrows the same set, which is the whole point of replacing the native
   * ones: a native filter runs on the server and re-renders, then this facet paints over
   * the result, so the two could never combine. */
  ModelFacet.prototype.applyFacets = function (indexes) {
    var self = this;
    return indexes.filter(function (index) {
      var entry = self.catalog[index];
      if (!entry) return false;
      // On a search page the query is a filter like any other, and it has to narrow every
      // path — including a model or part number resolved straight from the search box,
      // which reaches the catalogue without passing through `inScope`.
      if (self.searchSlots && self.searchSlots[index] !== true) return false;
      if (self.partTypes.length && self.partTypes.indexOf(self.slotType[index]) === -1) {
        return false;
      }
      if (self.stock === "in" && !entry.a) return false;
      if (self.stock === "out" && entry.a) return false;
      if (self.minPrice !== null && entry.p < self.minPrice) return false;
      if (self.maxPrice !== null && entry.p > self.maxPrice) return false;
      return true;
    });
  };

  /* The set the shopper is standing in: a collection on a collection page, the products a
   * query matched on a search page. Both narrow the same way, which is why the search
   * page needed no second code path — only a second way of answering this. */
  ModelFacet.prototype.inScope = function (indexes) {
    var self = this;
    if (this.searchSlots) {
      return indexes.filter(function (index) {
        return self.searchSlots[index] === true;
      });
    }
    if (!this.collection) return indexes;
    return indexes.filter(function (index) {
      var entry = self.catalog[index];
      return entry && entry.c && entry.c.indexOf(self.collection) !== -1;
    });
  };

  ModelFacet.prototype.loadNextPage = function () {
    var self = this;
    if (this.page >= this.pages) return Promise.resolve();

    return this.fetchJSON("brand-" + this.brandSlug + "-" + (this.page + 1) + ".json").then(
      function (page) {
        if (!page) {
          self.pages = self.page;
          self.renderModels();
          return;
        }
        self.page = page.page;
        self.pages = page.pages;

        page.m.forEach(function (entry) {
          var hits = self.inScope(entry.p);
          if (hits.length) self.models.push({ n: entry.n, p: hits });
        });

        // Collection scoping can empty a whole page, so keep pulling until there is
        // enough to show or the brand runs out.
        if (self.models.length < self.modelVisible && self.page < self.pages) {
          return self.loadNextPage();
        }
        self.renderModels();
        if (!self.model) self.showBrandResults();
      }
    );
  };

  /* AZP-7424 — the rest of the brand's models, pulled in one go so the search box can
   * answer for the whole list and "No results found" means what it says.
   *
   * A page holds 3000 models, so for every brand but eleven this is already a no-op.
   * For the largest it is not: Kenmore is 37168 models over 13 pages, 100 KB each. They
   * are fetched together rather than one after the next — `pages` is already known from
   * `brands.json`, so nothing here needs the previous response — which turns a dozen
   * round trips into one wait.
   *
   * Deliberately not `loadNextPage`: that one repaints the brand's results when it
   * lands, and a keystroke must not touch the product grid. */
  ModelFacet.prototype.loadAllModels = function () {
    var self = this;
    if (!this.brandSlug || this.page >= this.pages) return Promise.resolve();
    if (this.loadingAll) return this.loadingAll;

    var epoch = this.modelEpoch;
    var slug = this.brandSlug;
    var from = this.page + 1;
    var wanted = [];
    for (var number = from; number <= this.pages; number++) {
      wanted.push(this.fetchJSON("brand-" + slug + "-" + number + ".json"));
    }

    this.loadingAll = Promise.all(wanted).then(function (pages) {
      if (epoch !== self.modelEpoch) return;
      // Pages arrive in catalogue order, so appending them in order keeps the list A to
      // Z. A hole would silently drop the models behind it, so loading stops there and
      // `page` records only what did arrive. `pages` is left alone on purpose: the card
      // then still reads "20 of 9000+ models" with Load more offered, and the next
      // keystroke tries the missing pages again.
      for (var at = 0; at < pages.length; at++) {
        if (!pages[at]) break;
        pages[at].m.forEach(function (entry) {
          var hits = self.inScope(entry.p);
          if (hits.length) self.models.push({ n: entry.n, p: hits });
        });
        self.page = from + at;
      }
    }).then(function () {
      if (epoch === self.modelEpoch) self.loadingAll = null;
    });

    return this.loadingAll;
  };

  /* The models the search box leaves on screen. An empty keyword is the whole list,
   * which is what clearing the field has to restore. */
  ModelFacet.prototype.matchedModels = function () {
    var needle = this.modelQuery;
    if (!needle) return this.models;
    return this.models.filter(function (entry) {
      return entry.n.toLowerCase().indexOf(needle) !== -1;
    });
  };

  /* AZP-7424 — once the list has grown past the viewport cap it scrolls on its own, and
   * appending the next twenty then moves nothing on screen: same height, same rows in
   * view, button in the same place. Put the first of the new rows at the top instead.
   * A no-op while the list is still short enough to grow, which is what it should be —
   * the page itself moves there.
   *
   * Measured against the container rather than `offsetTop`, which answers to the
   * nearest positioned ancestor and not to the list. */
  ModelFacet.prototype.revealModel = function (index) {
    if (!this.list) return;
    var row = this.list.children[index];
    if (!row) return;
    this.list.scrollTop +=
      row.getBoundingClientRect().top - this.list.getBoundingClientRect().top;
  };

  /* A keyword belongs to the list it was typed over; carrying it to the next brand
   * would open that brand on "No results found". */
  ModelFacet.prototype.resetModelSearch = function () {
    this.modelQuery = "";
    if (this.search) this.search.value = "";
  };

  /* Every product the brand covers inside this collection, deduplicated. Picking a
   * brand is already a filter — waiting for a model before touching the grid would
   * leave the shopper looking at the unfiltered collection. */
  ModelFacet.prototype.brandResults = function () {
    var seen = {};
    var indexes = [];
    this.models.forEach(function (entry) {
      entry.p.forEach(function (index) {
        if (seen[index]) return;
        seen[index] = true;
        indexes.push(index);
      });
    });
    return indexes.sort(function (a, b) {
      return a - b;
    });
  };


  /* ---- AZP-7303: Product and Component ---- */

  /* Collection handle back to brand slug.
   *
   * Two of the 77 brands carry a numeric suffix the index builder added to break a
   * duplicate name — `thermador-1`, `simplicity-1` — which the collection handle
   * (`thermador-brand`) does not. Measured on the live index: 75 slugs map straight
   * across and those two do not, so the stripped form is registered as well. An exact
   * slug always wins, because it is written unconditionally. */
  ModelFacet.prototype.brandCollectionMap = function () {
    if (this.brandCollections) return this.brandCollections;
    var map = {};
    Object.keys(this.brands).forEach(function (slug) {
      var base = slug.replace(/-\d+$/, "");
      if (base !== slug && map[base + "-brand"] === undefined) {
        map[base + "-brand"] = slug;
      }
      map[slug + "-brand"] = slug;
    });
    this.brandCollections = map;
    return map;
  };

  /* The brand a brand-collection page already stands for.
   *
   * A shopper who picked "Whirlpool Brand" out of the search suggestions has chosen a
   * brand as surely as one who ticked the Brand facet, and the ticket asks for the same
   * consequence: Product and Component narrow to it. Kept apart from `brandSlug`, which
   * means "this facet is filtering the grid" — the page already renders exactly this
   * brand's products, so claiming the grid would only replace it with itself and
   * disable the sort orders the server can still do better. */
  ModelFacet.prototype.resolvePageBrand = function () {
    if (!this.collection) return null;
    var slug = this.brandCollectionMap()[this.collection];
    return slug === undefined ? null : slug;
  };

  /* Whichever brand is in force, however it was chosen. */
  ModelFacet.prototype.activeBrand = function () {
    return this.brandSlug || this.pageBrand || null;
  };

  /* AZP-7417 — whether the shopper came in through Brand, which decides the action link
   * and the one chip that cannot be cleared.
   *
   * A brand collection page is a brand entry by definition. Anywhere else the brand
   * alone cannot say: "arrived under Samsung" and "ticked Samsung on this page" both
   * leave `#az_brand=samsung` and nothing else. So the links leading off a brand entry
   * carry `az_from=brand` with them, and a brand ticked on a category page writes no
   * such marker — its absence is the product entry.
   *
   * Guarded by `activeBrand()`: a marker left over from a brand that is no longer in
   * force would otherwise leave the header offering to change a brand there isn't. */
  ModelFacet.prototype.entryIsBrand = function () {
    if (!this.activeBrand()) return false;
    return !!this.pageBrand || this.param(ENTRY_KEY) === "brand";
  };

  /* How many products each category holds, under the brand in force.
   *
   * Counted over the whole catalogue rather than the collection being browsed: picking
   * a Product navigates away from it, so the number that matters is what the target
   * page will show, not what the current one does. */
  ModelFacet.prototype.categoryCounts = function () {
    var brand = this.activeBrand();
    var counts = {};
    this.catalog.forEach(function (entry) {
      if (!entry.c) return;
      // The index writes brands onto a product only where it knows them, so a brand
      // filter necessarily drops the rest — the same trade `scopeBrands` makes.
      if (brand && (!entry.b || entry.b.indexOf(brand) === -1)) return;
      entry.c.forEach(function (handle) {
        counts[handle] = (counts[handle] || 0) + 1;
      });
    });
    return counts;
  };

  /* Fifteen options are rendered open and the rest folded away, but that cut was made by
   * Liquid, which cannot know which branches are empty. Emptying some of the first
   * fifteen would leave the list short with a "show all" button under it, so the fold is
   * re-cut here over what is actually left. */
  ModelFacet.prototype.reflowCategories = function (list) {
    if (!list) return;
    var items = list.querySelectorAll(".az-cat-item");
    var shown = 0;
    var folded = 0;
    Array.prototype.forEach.call(items, function (item) {
      if (item.classList.contains("az-cat-empty")) return;
      if (shown < CATEGORY_STEP) {
        item.classList.remove("az-cat-overflow");
        shown += 1;
      } else {
        folded += 1;
      }
    });

    var button = list.parentNode && list.parentNode.querySelector("[data-category-more]");
    if (!button) return;
    button.hidden = folded === 0;
    button.textContent = "Show " + folded + " more";
  };

  /* The counts, the empty branches, and the brand the links carry.
   *
   * A category link is a plain navigation to a collection page, so a brand chosen here
   * would be lost on the way. The fragment the facet already writes for itself is
   * appended to every link, which is what makes "Whirlpool, then Dryer" keep the
   * Whirlpool. */
  ModelFacet.prototype.updateCategories = function () {
    if (!this.catalog) return;
    var counts = this.categoryCounts();
    // A brand implied by the page is carried too: stepping from "Whirlpool Brand" into
    // Dryer must stay Whirlpool, and the Dryer page has no other way to know.
    var brand = this.activeBrand();
    // AZP-7417 — the way in travels with the brand, so a Product chosen under a brand
    // still answers to "Change brand" on the page it lands on.
    var fromBrand = this.entryIsBrand();
    var hash = brand ? "#" + BRAND_KEY + "=" + encodeURIComponent(brand) : "";
    if (hash && fromBrand) hash += "&" + ENTRY_KEY + "=brand";

    Array.prototype.forEach.call(
      document.querySelectorAll("[data-category-handle]"),
      function (link) {
        var count = counts[link.dataset.categoryHandle] || 0;
        // Written, but not seen: `bluestars-custom.css` hides every facet count in this
        // sidebar. What the number is actually for is the empty branches below.
        var badge = link.querySelector("[data-category-count]");
        if (badge) {
          badge.textContent = count;
          badge.hidden = false;
        }
        // Only list rows can be hidden. A chip is a value already chosen, and it is
        // not in a row — it holds the x that clears it.
        var item = link.closest(".az-cat-item");
        if (item) item.classList.toggle("az-cat-empty", count === 0);
      }
    );

    // The options, and the x's on the chips that clear them. The Brand chip's x is
    // skipped: it is either a fixed link out of a brand page, or handled in JS.
    Array.prototype.forEach.call(
      document.querySelectorAll("[data-category-handle], [data-category-chip] .az-cat-chip-clear"),
      function (link) {
        if (!link.getAttribute("href")) return;
        if (!link.dataset.baseHref) link.dataset.baseHref = link.getAttribute("href");
        link.setAttribute("href", link.dataset.baseHref + hash);
      }
    );

    // AZP-7417 — the header's action link, corrected for the brand carried in the
    // fragment.
    //
    // Liquid names it from the URL path alone: a page inside the category tree gets
    // "Change product". But a shopper who came in through Brand carries that brand in
    // `#az_brand=`, and a fragment never reaches the server, so the page cannot know
    // its own way in. Here it can — "chọn đường filter nào thì change filter đó".
    //
    // What Liquid rendered is kept, so clearing the brand puts the original label back
    // rather than a guess. The link is absent on /collections/all and on search, where
    // the page belongs to no branch; nothing is created for those.
    var change = document.querySelector(".az-filters-change");
    if (change) {
      if (!change.dataset.baseLabel) {
        change.dataset.baseLabel = change.textContent.trim();
        change.dataset.baseHref = change.getAttribute("href");
      }
      change.textContent = fromBrand ? "Change brand" : change.dataset.baseLabel;
      change.setAttribute("href", fromBrand ? "/pages/brands" : change.dataset.baseHref);
    }

    // AZP-7417 — and the chip that link answers for loses its x, for the same reason:
    // it is the way in, so the link above is what changes it. Every other chip keeps
    // its x — including a Brand ticked on a product entry, which is an ordinary filter
    // there and has to come off again.
    var fixedChip = document.querySelector(
      fromBrand
        ? '[data-name="az_brand"] .az-cat-chip'
        : '[data-name="az_product"] .az-cat-chip'
    );
    Array.prototype.forEach.call(
      document.querySelectorAll(".az-cat-chip"),
      function (chip) {
        chip.classList.toggle("az-cat-chip-fixed", chip === fixedChip);
      }
    );

    this.reflowCategories(this.productList);
    this.reflowCategories(this.componentList);
  };

  ModelFacet.prototype.renderAvailability = function () {
    if (!this.availabilityList) return;
    var self = this;
    var counts = { in: 0, out: 0 };
    this.catalog.forEach(function (entry) {
      if (!self.collection || (entry.c || []).indexOf(self.collection) !== -1) {
        counts[entry.a ? "in" : "out"] += 1;
      }
    });
    this.availabilityList.innerHTML =
      optionHTML("stock", "in", "In stock", counts.in, this.stock === "in") +
      optionHTML("stock", "out", "Out of stock", counts.out, this.stock === "out");
  };

  /* AZP-7303 — the slider needs an upper bound, and the amounts need something to show
   * before the shopper types. Both are the dearest thing on the page, rounded up so the
   * top of the range is reachable rather than a fraction short of it. */
  ModelFacet.prototype.renderPriceHint = function () {
    var self = this;
    var prices = this.catalog
      .filter(function (entry) {
        return !self.collection || (entry.c || []).indexOf(self.collection) !== -1;
      })
      .map(function (entry) { return entry.p; });
    if (!prices.length) return;

    this.priceCeiling = Math.ceil(Math.max.apply(null, prices));
    [this.priceMinRange, this.priceMaxRange].forEach(function (input) {
      if (input) input.max = self.priceCeiling;
    });
    if (this.priceFrom) this.priceFrom.placeholder = "0";
    if (this.priceTo) this.priceTo.placeholder = this.priceCeiling;
    if (this.priceFrom && this.minPrice !== null) this.priceFrom.value = this.minPrice;
    if (this.priceTo && this.maxPrice !== null) this.priceTo.value = this.maxPrice;
    this.syncPriceSlider();
    // Only now: the chip names the ceiling when no upper bound was set, and the
    // ceiling is what this function just worked out.
    this.renderPrice();
  };

  /* The slider and the two amounts are two views of one range; whichever the shopper
   * touched, this puts the other back in step and repaints the fill. Neither filters:
   * that waits for Apply. */
  ModelFacet.prototype.syncPriceSlider = function (from) {
    var ceiling = this.priceCeiling || 0;
    if (!ceiling || !this.priceMinRange || !this.priceMaxRange) return;

    var low = this.minPrice === null ? 0 : this.minPrice;
    var high = this.maxPrice === null ? ceiling : this.maxPrice;
    // Dragging one thumb past the other would invert the range; the pushed thumb stops
    // where the other stands instead.
    if (low > high) {
      if (from === "min") high = low;
      else low = high;
    }
    low = Math.max(0, Math.min(low, ceiling));
    high = Math.max(0, Math.min(high, ceiling));

    this.priceMinRange.value = low;
    this.priceMaxRange.value = high;
    if (this.priceFill) {
      this.priceFill.style.insetInlineStart = (low / ceiling) * 100 + "%";
      this.priceFill.style.insetInlineEnd = 100 - (high / ceiling) * 100 + "%";
    }
    return { low: low, high: high };
  };

  function escapeHTML(text) {
    return String(text).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;");
  }

  /* AZP-7303 — a chosen Brand or price range collapses to the same pill Product and
   * Component use (Figma "Menu item", 5573-36905 and 5578-17659). Mirrors
   * `collection-category-chip.liquid`; keep the two in step.
   *
   * The x has two jobs. A filter this facet holds is cleared in place, so it carries
   * the marker its click handler watches for. A brand the *page* stands for cannot be
   * cleared in place — leaving it means leaving the page — so that one is a plain link
   * out to /collections/all.
   *
   * `labelHTML` is inserted as markup so the price chip can put an arrow between its
   * two amounts; callers escape anything that came from data. */
  function chipHTML(labelHTML, ariaLabel, clearURL, clearMarker) {
    return (
      '<li class="az-cat-chip-item">' +
      '<span class="az-cat-chip">' +
      '<span class="az-cat-chip-label">' + labelHTML + "</span>" +
      '<a class="az-cat-chip-clear" href="' + clearURL + '"' +
      (clearMarker ? " " + clearMarker : "") +
      ' aria-label="Clear ' + escapeHTML(ariaLabel) + '">' +
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
      '<path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>' +
      "</svg></a></span></li>"
    );
  }

  /* Liquid decides a card's open state at render time, from the URL alone, so a filter
   * restored from the fragment — `#az_brand=`, which every Product and Component link
   * now carries, or `#az_min=`/`#az_max=` — would put its chip inside a shut card.
   * `collapsible-expandable` reads `expanded` once, in `connectedCallback`, so the
   * attribute is no use this late; `expand()` is.
   *
   * `expand()` then pins `max-height` to the height it measured, and `.facet-content`
   * is `overflow: hidden`. That height is right only until the content changes — a long
   * brand name wrapping on a narrow screen, or the price editor coming back when its
   * chip is cleared. Both cards are open because something is chosen and there is
   * nothing left to animate, so the cap comes off. */
  function openFacetCard(within) {
    var card = within && within.closest("collapsible-expandable");
    if (!card || typeof card.expand !== "function") return;
    if (card.getAttribute("expanded") !== "true") card.expand();
    var content = card.querySelector(".facet-content");
    if (content) content.style.maxHeight = "none";
  }

  ModelFacet.prototype.openBrandCard = function () {
    openFacetCard(this.brandList);
  };

  /* AZP-7424 — the same cap, on the Model number card. Its content now changes height
   * after `expand()` measured it: the search row appears with the first brand, and the
   * list shrinks to whatever a keyword matched. A card expanded while it still said
   * "Pick a brand" was pinned at 27px and clipped everything that arrived after.
   *
   * Only an already-open card: `openFacetCard` would prise a shut one open, and this
   * runs on every repaint. */
  ModelFacet.prototype.releaseModelCard = function () {
    var card = this.list && this.list.closest("collapsible-expandable");
    if (!card || card.getAttribute("expanded") !== "true") return;
    var content = card.querySelector(".facet-content");
    if (content) content.style.maxHeight = "none";
  };

  /* AZP-7303 — an applied range collapses to `$0 → $203 ✕` (Figma 5578-17654), and the
   * slider, the amounts and Apply give way to it. Clearing the chip brings them back.
   *
   * The two ends are always both shown, even when only one was set: `Apply` drops a
   * bound that covers everything, but "$0 → $20" still describes the range better than
   * "$20" alone. */
  ModelFacet.prototype.renderPrice = function () {
    if (!this.priceEditor || !this.priceChipList) return;

    var active = this.minPrice !== null || this.maxPrice !== null;
    this.priceEditor.hidden = active;
    this.priceChipList.hidden = !active;

    if (!active) {
      this.priceChipList.innerHTML = "";
      return;
    }

    var low = this.minPrice === null ? 0 : this.minPrice;
    var high = this.maxPrice === null ? this.priceCeiling || 0 : this.maxPrice;
    var arrow =
      '<svg class="az-price-chip-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
      '<path d="M8.77713 4L12.666 8L8.77713 12M12.666 8H3.33268" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
      "</svg>";

    this.priceChipList.innerHTML = chipHTML(
      "$" + low + arrow + "$" + high,
      "price range $" + low + " to $" + high,
      "#",
      "data-price-clear"
    );
    openFacetCard(this.priceChipList);
  };

  /* Back to the editor, with the range it had. Only the chip's x reaches here, so the
   * card is open and stays open. */
  ModelFacet.prototype.clearPrice = function () {
    this.minPrice = null;
    this.maxPrice = null;
    if (this.priceFrom) this.priceFrom.value = "";
    if (this.priceTo) this.priceTo.value = "";
    this.syncPriceSlider();
    this.refilterInner();
    openFacetCard(this.priceEditor);
  };

  ModelFacet.prototype.renderBrands = function () {
    if (!this.brandList) return;
    var self = this;

    var fixed = this.activeBrand();
    if (fixed && this.brands[fixed]) {
      var name = this.brands[fixed].b;
      this.brandList.innerHTML = chipHTML(
        escapeHTML(name),
        name,
        this.brandSlug ? "#" : "/collections/all",
        this.brandSlug ? "data-brand-clear" : ""
      );
      if (this.brandMore) this.brandMore.hidden = true;
      this.openBrandCard();
      return;
    }

    // AZP-7303 — the brand in force can sit past the fifteenth: brands are listed A to
    // Z, so Whirlpool never makes the first window. Clicking one always leaves it
    // visible, but arriving with `az_brand` already set does not — and that is now the
    // normal way in, because every Product and Component link carries it. The window
    // stretches to reach it, the way `renderTypes` does for a restored Part Type.
    var active = this.activeBrand();
    var visible = this.brandVisible;
    var activeAt = active ? this.brandSlugs.indexOf(active) : -1;
    if (activeAt >= visible) visible = activeAt + 1;
    var shown = this.brandSlugs.slice(0, visible);

    // AZP-7303 — a brand-collection page shows its own brand ticked. Narrowing Product
    // and Component with nothing on screen to explain why reads as missing data.
    this.brandList.innerHTML = shown
      .map(function (slug) {
        return optionHTML("brand", slug, self.brands[slug].b, null, slug === active);
      })
      .join("");

    var remaining = this.brandSlugs.length - shown.length;
    if (this.brandMore) this.brandMore.hidden = remaining <= 0;
    if (this.brandMore) this.brandMore.textContent = "Show " + Math.min(remaining, BRAND_STEP) + " more brands";
  };

  /* Slugs back to labels. The labels are what `applyFacets` compares against, and only
   * the types this query offers can be selected, so an unknown slug is simply dropped. */
  ModelFacet.prototype.typesFromParam = function (value) {
    if (!value) return [];
    var wanted = {};
    value.split(",").forEach(function (slug) {
      if (slug) wanted[slug] = true;
    });
    return this.typeNames.filter(function (name) {
      return wanted[typeSlug(name)] === true;
    });
  };

  /* Several types can be on at once — they are alternatives, not a refinement of each
   * other, and the design draws checkboxes rather than a single-choice list. */
  ModelFacet.prototype.renderTypes = function () {
    if (!this.typeList) return;
    var self = this;
    // A type restored from the URL can sit past the tenth, and a checkbox the shopper
    // cannot see is a filter they cannot turn off. The window stretches to reach it.
    var visible = this.typeVisible;
    this.partTypes.forEach(function (name) {
      var at = self.typeNames.indexOf(name);
      if (at >= visible) visible = at + 1;
    });
    var shown = this.typeNames.slice(0, visible);

    this.typeList.innerHTML = shown
      .map(function (name) {
        return optionHTML("type", name, name, null, self.partTypes.indexOf(name) !== -1);
      })
      .join("");

    var remaining = this.typeNames.length - shown.length;
    if (!this.typeMore) return;
    this.typeMore.hidden = remaining <= 0;
    this.typeMore.textContent =
      "Show " + Math.min(remaining, TYPE_STEP) + " more part types";
  };

  ModelFacet.prototype.renderModels = function () {
    if (!this.list) return;
    var matching = this.matchedModels();
    var shown = matching.slice(0, this.modelVisible);

    // AZP-7424 — nothing to search before a brand is picked, so the field appears with
    // the first options rather than sitting over an empty card.
    if (this.searchRow) this.searchRow.hidden = !this.models.length;

    if (!shown.length) {
      this.list.innerHTML = "";
      if (this.more) this.more.hidden = true;
      if (this.modelQuery) this.setStatus("No results found");
      else if (this.brandSlug) this.setStatus("No model for this brand in this collection.");
      this.releaseModelCard();
      return;
    }

    var self = this;
    this.list.innerHTML = shown
      .map(function (entry) {
        return optionHTML("model", entry.n, entry.n, entry.p.length, entry.n === self.model);
      })
      .join("");

    // Count only what this facet can actually offer: models whose parts are in the
    // collection being browsed. The brand's store-wide total would promise entries that
    // no amount of "load more" can reveal here. With a keyword typed the count is of
    // what matched it, which is the list the shopper is looking at.
    var matched = matching.length;
    var allLoaded = this.page >= this.pages;
    var label;
    if (!allLoaded) {
      label = shown.length + " of " + matched + "+ models";
    } else if (shown.length < matched) {
      label = shown.length + " of " + matched + " models";
    } else {
      label = matched + (matched === 1 ? " model" : " models");
    }
    this.setStatus(label);
    if (this.more) this.more.hidden = matched <= this.modelVisible && allLoaded;
    this.releaseModelCard();
  };

  ModelFacet.prototype.setStatus = function (message) {
    if (!this.status) return;
    this.status.textContent = message || "";
    this.status.hidden = !message;
  };

  /* ---- selection ---- */

  ModelFacet.prototype.selectBrand = function (slug) {
    this.brandSlug = slug;
    // A model belongs to the brand it was picked under; carrying it across would leave
    // the grid showing the previous brand's result and skip the new brand's own.
    this.model = null;
    this.results = [];
    if (this.clearButton) this.clearButton.hidden = true;
    this.models = [];
    this.page = 0;
    this.pages = this.brands[slug] ? this.brands[slug].pages : 1;
    this.modelVisible = MODEL_STEP;
    // AZP-7424 — the list this keyword was typed over is gone, and any page load it
    // started must not land in the new brand's.
    this.modelEpoch += 1;
    this.loadingAll = null;
    this.resetModelSearch();
    this.renderBrands();
    // AZP-7303 — Product and Component now answer for this brand only.
    this.updateCategories();
    this.writeState();
    this.setStatus("Loading models…");
    return this.loadNextPage();
  };

  ModelFacet.prototype.clearBrand = function () {
    this.brandSlug = null;
    this.models = [];
    this.page = 0;
    this.modelEpoch += 1;
    this.loadingAll = null;
    this.resetModelSearch();
    if (this.list) this.list.innerHTML = "";
    if (this.more) this.more.hidden = true;
    if (this.searchRow) this.searchRow.hidden = true;
    this.renderBrands();
    this.updateCategories();
    this.model = null;
    this.results = [];
    if (this.clearButton) this.clearButton.hidden = true;
    this.writeState();
    // Not `restoreGrid()`: price and availability may still be on, and putting the whole
    // collection back would silently drop them.
    this.applyToNativeGrid();
    if (!this.hasOwnFilter()) this.setStatus("Pick a brand to see the models it covers.");
  };


  /* A model number can arrive without a brand — the search box sends one straight here —
   * so it is resolved from the prefix shards rather than from a brand's pages. The shards
   * chain: a prefix that grew too large is replaced by a stub naming a deeper one, because
   * every Kenmore model starts `110`. */
  /* Models are filed in one sorted sequence cut into shards of roughly equal size;
   * `models-manifest.json` holds the first key of each. Finding the right shard is a
   * binary search over ~48 strings, so a direct model lookup costs the manifest once and
   * one shard thereafter.
   *
   * Separators are ignored when picking the shard and nowhere else: the models inside
   * stay verbatim, because 617 of them collide once separators go. */
  function sortKey(text) {
    return String(text || "").toUpperCase().replace(/[^A-Z0-9]+/g, "");
  }

  ModelFacet.prototype.shard = function (query) {
    var self = this;
    var manifest = this.manifest;
    if (!manifest) {
      return this.fetchJSON("models-manifest.json").then(function (data) {
        self.manifest = data || { b: [] };
        return self.shard(query);
      });
    }

    var bounds = manifest.b || [];
    if (!bounds.length) return Promise.resolve(null);
    var key = sortKey(query);
    var low = 0;
    var high = bounds.length - 1;
    while (low < high) {
      var mid = Math.ceil((low + high) / 2);
      if (bounds[mid] <= key) low = mid;
      else high = mid - 1;
    }
    var name = "models-p" + (low < 10 ? "0" + low : low);

    if (this.shards[name] !== undefined) return Promise.resolve(this.shards[name]);
    return this.fetchJSON(name + ".json").then(function (data) {
      self.shards[name] = data;
      return data;
    });
  };

  ModelFacet.prototype.showModelDirect = function (model) {
    var self = this;
    this.shard(model).then(function (shard) {
      var hits = shard && shard.m ? shard.m[model] : null;
      if (!hits || !hits.length) {
        self.setStatus("No part fits " + model + ".");
        return;
      }
      // The shard indexes its own product list, not the catalogue, so the handles are
      // mapped back through it.
      var slots = hits
        .map(function (index) {
          return self.handleSlot[shard.p[index].h];
        })
        .filter(function (slot) {
          return slot !== undefined;
        });
      if (!slots.length) {
        self.setStatus("No part fits " + model + ".");
        return;
      }
      self.model = model;
      self.results = self.finalise(slots);
      self.resultPage = 1;
      self.renderResults(model);
      if (self.clearButton) self.clearButton.hidden = false;
    });
  };

  ModelFacet.prototype.showPartNumber = function (number) {
    var self = this;
    this.fetchJSON("partnumbers.json").then(function (data) {
      var slots = data && data.n ? data.n[number] : null;
      if (!slots || !slots.length) {
        self.setStatus("No part matches " + number + ".");
        return;
      }
      self.results = self.finalise(slots);
      self.resultPage = 1;
      self.renderResults("part " + number);
      if (self.clearButton) self.clearButton.hidden = false;
    });
  };

  ModelFacet.prototype.showBrandResults = function () {
    if (!this.grid || !this.brandSlug) return;
    this.results = this.finalise(this.brandResults());
    this.resultPage = 1;
    if (!this.results.length) {
      // An empty result is an answer, not a reason to show everything.
      if (this.grid) this.grid.innerHTML = "";
      this.renderCount(0);
      this.setThemePagination(false);
      this.setStatus("No parts match this selection.");
      return;
    }
    this.renderResults(this.brands[this.brandSlug].b);
  };

  ModelFacet.prototype.restoreGrid = function () {
    this.renderToken++;
    if (this.grid) this.grid.innerHTML = this.gridHTML;
    var pagination = document.querySelector("[data-model-pagination]");
    if (pagination) pagination.remove();
    this.restoreCount();
    this.setThemePagination(true);
  };

  ModelFacet.prototype.selectModel = function (model) {
    var entry = null;
    for (var i = 0; i < this.models.length; i++) {
      if (this.models[i].n === model) {
        entry = this.models[i];
        break;
      }
    }
    if (!entry || !this.grid) return;

    this.model = model;
    this.results = this.finalise(entry.p);
    this.resultPage = 1;
    this.writeState();
    if (this.clearButton) this.clearButton.hidden = false;
    this.renderModels();
    this.renderResults();
  };

  /* Cards come from the theme, not from a template kept here: a copy of `card-product`
   * would lose the image, badges, stock line and add-to-cart the moment either side
   * changed. One request per visible card, and only for the page being shown. */
  ModelFacet.prototype.renderResults = function (subject) {
    var self = this;
    var total = this.results.length;
    var pages = Math.max(1, Math.ceil(total / RESULTS_PER_PAGE));
    var page = Math.min(Math.max(this.resultPage, 1), pages);
    this.resultPage = page;

    var slice = this.results.slice((page - 1) * RESULTS_PER_PAGE, page * RESULTS_PER_PAGE);
    var token = ++this.renderToken;
    this.setStatus(total + " part(s) for " + (subject || this.model));
    this.grid.innerHTML = "";
    this.renderCount(total);

    slice.forEach(function (index, position) {
      var product = self.catalog[index];
      var placeholder = document.createElement("li");
      placeholder.className = "grid-item model-facet-loading";
      self.grid.appendChild(placeholder);

      fetch("/products/" + product.h + "?section_id=" + CARD_SECTION)
        .then(function (response) {
          return response.ok ? response.text() : null;
        })
        .then(function (html) {
          if (token !== self.renderToken) return; // a newer selection took over
          if (!html) {
            placeholder.remove();
            return;
          }
          var holder = document.createElement("div");
          holder.innerHTML = html.trim();
          var card = holder.querySelector("[data-model-result-card]") || holder.firstElementChild;
          if (card) placeholder.replaceWith(card);
          else placeholder.remove();
        })
        .catch(function () {
          placeholder.remove();
        });
      return position;
    });

    this.renderPagination(page, pages);
  };

  /* The theme already prints the result count in `#ProductCount`; rewriting it keeps the
   * filtered view in the same place and the same type as an unfiltered one, and the
   * original text is put back when the filter clears. */
  ModelFacet.prototype.renderCount = function (total) {
    var node = document.getElementById("ProductCount");
    if (!node) return;
    if (this.originalCount === null) this.originalCount = node.textContent;
    // AZP-7078 — the search page counts "Showing N Parts", so a filtered count has to
    // read the same way; a collection keeps the wording it had.
    node.textContent = this.isSearch
      ? "Showing " + total + (total === 1 ? " Part" : " Parts")
      : total + (total === 1 ? " Product" : " Products");
  };

  ModelFacet.prototype.restoreCount = function () {
    var node = document.getElementById("ProductCount");
    if (node && this.originalCount !== null) node.textContent = this.originalCount;
  };

  /* The server-rendered page keeps its own pagination for the unfiltered collection —
   * nine pages of dryer parts stay on screen next to a single filtered result unless it
   * is put away while the filter is on. */
  ModelFacet.prototype.setThemePagination = function (visible) {
    document.querySelectorAll(".pagination:not([data-model-pagination])").forEach(
      function (node) {
        node.hidden = !visible;
      }
    );
  };

  ModelFacet.prototype.renderPagination = function (page, pages) {
    var existing = document.querySelector("[data-model-pagination]");
    if (existing) existing.remove();
    this.setThemePagination(false);
    if (pages <= 1) return;

    var wrap = document.createElement("div");
    wrap.className = "pagination";
    wrap.setAttribute("data-model-pagination", "");

    var parts = [];
    if (page > 1) parts.push({ label: "‹", page: page - 1, cls: "page-number page-number-prev" });
    for (var n = 1; n <= pages; n++) {
      if (pages > 7 && n > 2 && n < pages - 1 && Math.abs(n - page) > 1) {
        if (parts[parts.length - 1] && parts[parts.length - 1].gap) continue;
        parts.push({ label: "…", gap: true, cls: "page-number page-number-gap" });
        continue;
      }
      parts.push({
        label: String(n),
        page: n,
        cls: n === page ? "page-number page-number-active" : "page-number",
      });
    }
    if (page < pages) parts.push({ label: "›", page: page + 1, cls: "page-number page-number-next" });

    var self = this;
    parts.forEach(function (part) {
      var node = document.createElement(part.page ? "button" : "span");
      node.className = part.cls;
      node.textContent = part.label;
      if (part.page) {
        node.type = "button";
        node.addEventListener("click", function () {
          self.resultPage = part.page;
          self.renderResults();
          self.grid.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
      wrap.appendChild(node);
    });

    this.grid.parentNode.insertBefore(wrap, this.grid.nextSibling);
  };

  ModelFacet.prototype.clearModel = function () {
    this.model = null;
    this.results = [];
    if (this.clearButton) this.clearButton.hidden = true;
    this.writeState();
    this.renderModels();
    // Fall back to whatever is still selected — the brand, or price and availability on
    // their own — rather than all the way to the unfiltered collection.
    if (this.brandSlug) this.showBrandResults();
    else this.applyToNativeGrid();
  };


  /* ---- URL state ---- */

  /* State rides in the URL so a reload or a shared link restores it. A query param would
   * also survive the theme's re-render, but it navigates; the facet must feel like the
   * native ones, which never reload the page. */
  ModelFacet.prototype.param = function (key) {
    var search = new URLSearchParams(window.location.search).get(key);
    if (search) return search;
    var match = window.location.hash.match(new RegExp("[#&]" + key + "=([^&]+)"));
    return match ? decodeURIComponent(match[1]) : null;
  };

  /* Slugs are [a-z0-9-] only, so a comma joins them unambiguously and nothing needs
   * escaping. */
  ModelFacet.prototype.encodedTypes = function () {
    return this.partTypes.map(typeSlug).join(",");
  };

  /* Written to both the fragment and the theme's hidden inputs: the fragment survives a
   * reload, the inputs make `facets.js` carry the state through its next FormData read
   * when a sort or a native filter fires. */
  ModelFacet.prototype.writeState = function () {
    var parts = [];
    if (this.brandSlug) {
      parts.push(BRAND_KEY + "=" + encodeURIComponent(this.brandSlug));
      // AZP-7417 — kept so a reload does not quietly downgrade a brand entry to a
      // product one. Not written on a brand collection page: `pageBrand` already says
      // so there, and `brandSlug` is empty.
      if (this.entryIsBrand()) parts.push(ENTRY_KEY + "=brand");
    }
    if (this.model) parts.push(MODEL_KEY + "=" + encodeURIComponent(this.model));
    if (this.sort) parts.push(SORT_KEY + "=" + encodeURIComponent(this.sort));
    if (this.partTypes.length) parts.push(TYPE_KEY + "=" + this.encodedTypes());
    if (this.stock) parts.push(AVAIL_KEY + "=" + this.stock);
    if (this.minPrice !== null) parts.push(MIN_KEY + "=" + this.minPrice);
    if (this.maxPrice !== null) parts.push(MAX_KEY + "=" + this.maxPrice);
    var url = window.location.pathname + window.location.search;
    history.replaceState(null, "", parts.length ? url + "#" + parts.join("&") : url);

    this.updateSortOptions();

    var values = {};
    values[BRAND_KEY] = this.brandSlug || "";
    values[MODEL_KEY] = this.model || "";
    values[TYPE_KEY] = this.partTypes.length ? this.encodedTypes() : "";
    values[AVAIL_KEY] = this.stock || "";
    values[MIN_KEY] = this.minPrice === null ? "" : this.minPrice;
    values[MAX_KEY] = this.maxPrice === null ? "" : this.maxPrice;
    Object.keys(values).forEach(function (name) {
      var input = document.querySelector('input[name="' + name + '"]');
      if (input) input.value = values[name];
    });
  };

  /* Applies whatever state the URL carries — on first load, and again after the theme
   * re-renders the sidebar, where the facet markup comes back empty. */
  /* Re-runs whichever view is active under the new facet values, without refetching. */
  /* Wraps a step so a thrown error lands in the facet's own status line. A silent throw
   * here leaves every list empty and reads as missing data — which cost most of a day
   * once already. */
  ModelFacet.prototype.guard = function (label, fn) {
    try {
      return fn();
    } catch (error) {
      if (this.status) {
        this.status.hidden = false;
        this.status.textContent = label + " failed: " + (error && error.message);
      }
      // Logged, shown, and swallowed: a failed facet must not take the rest of the page
      // with it, and re-throwing left every list frozen with no sign of why.
      console.error("[model-facet] " + label, error);
      return null;
    }
  };

  /* Price and availability are filters in their own right. Treating only brand and model
   * as "filtering" sent a sort change down the native path — reloading the page and
   * handing Shopify a sort over the whole collection — and left a URL carrying only
   * `az_min` or `az_stock` doing nothing at all on load. */
  ModelFacet.prototype.hasOwnFilter = function () {
    return Boolean(
      this.brandSlug ||
        this.model ||
        this.param(PART_KEY) ||
        this.partTypes.length ||
        this.stock ||
        this.minPrice !== null ||
        this.maxPrice !== null
    );
  };

  ModelFacet.prototype.refilter = function () {
    var self = this;
    return this.guard("Filter", function () {
      return self.refilterInner();
    });
  };

  ModelFacet.prototype.refilterInner = function () {
    this.renderAvailability();
    this.renderTypes();
    // AZP-7303 — the price card swaps between its editor and its chip on every change
    // of the range, including one restored from the URL.
    this.renderPrice();
    this.writeState();
    if (this.model && this.models.length) this.selectModel(this.model);
    else if (this.model) this.showModelDirect(this.model);
    else if (this.brandSlug) this.showBrandResults();
    else if (this.param(PART_KEY)) this.showPartNumber(this.param(PART_KEY));
    else this.applyToNativeGrid();
  };

  /* With no brand, model or part chosen there is no result set of our own, so the
   * page's own grid is narrowed instead of replaced. */
  ModelFacet.prototype.applyToNativeGrid = function () {
    var filtering =
      this.partTypes.length ||
      this.stock ||
      this.minPrice !== null ||
      this.maxPrice !== null;
    if (!filtering) return this.restoreGrid();

    var all = this.catalog.map(function (_, index) { return index; });
    this.results = this.finalise(this.inScope(all));
    this.resultPage = 1;
    this.renderResults("this selection");
  };

  ModelFacet.prototype.restore = function () {
    var self = this;

    // A part number arrives from the search box, which sends number queries here rather
    // than to `/search` — Shopify cannot index these, so its results page would be empty
    // and, having found nothing, would render no sidebar or pagination either.
    // AZP-7424 — a filter is only honoured while the card that shows and clears it is
    // rendered. Price and Availability are gone from the sidebar, so a link saved while
    // they still existed must not narrow the grid with no control left to switch off.
    this.stock = this.availabilityList ? this.param(AVAIL_KEY) || "" : "";
    this.partTypes = this.typesFromParam(this.param(TYPE_KEY));
    var min = this.priceEditor ? parseFloat(this.param(MIN_KEY)) : NaN;
    var max = this.priceEditor ? parseFloat(this.param(MAX_KEY)) : NaN;
    this.minPrice = isNaN(min) ? null : min;
    this.maxPrice = isNaN(max) ? null : max;
    this.renderAvailability();
    this.renderTypes();
    this.renderPriceHint();
    // The sidebar comes back from the server on every native re-render, so the counts
    // and the brand the category links carry have to be put back with it.
    this.updateCategories();

    var partNumber = this.param(PART_KEY);
    if (partNumber) return this.showPartNumber(partNumber);

    var brand = this.param(BRAND_KEY);
    if (!brand || !this.brands[brand]) {
      // A model with no brand comes from the search box; resolve it on its own.
      var loneModel = this.param(MODEL_KEY);
      if (loneModel) return this.showModelDirect(loneModel);
      this.writeState();
      // Price or availability on their own still narrow the page's own grid.
      if (this.hasOwnFilter()) this.applyToNativeGrid();
      return;
    }
    if (brand === this.brandSlug && this.models.length) {
      this.renderBrands();
      this.renderModels();
      if (this.model) this.selectModel(this.model);
      else this.showBrandResults();
      return;
    }

    var model = this.param(MODEL_KEY);
    var loading = this.selectBrand(brand);
    if (!loading || !loading.then || !model) return;

    loading.then(function () {
      // The model may sit past the first page, so keep loading until it turns up.
      (function seek() {
        var found = self.models.some(function (entry) {
          return entry.n === model;
        });
        if (found) return self.selectModel(model);
        if (self.page >= self.pages) return;
        self.loadNextPage().then(seek);
      })();
    });
  };

  /* ---- events ---- */

  /* The theme's sort select posts through `FacetsFilterForm`, which makes `facets.js`
   * re-render the sidebar and destroy this facet. Detaching it from the form and handling
   * the change here keeps the selection intact; with no facet active the choice is passed
   * straight back to Shopify, which sorts far better than the browser can. */
  ModelFacet.prototype.takeOverSort = function () {
    var self = this;
    var select = document.getElementById("collection-sort-order");
    if (!select) return;

    select.removeAttribute("form");
    // Removing the form attribute stops the theme submitting it, but `facets.js` also
    // listens for `input` on anything inside `.collection-actions-wrapper` — which is
    // where this select lives — and re-renders the whole sidebar, replacing every facet
    // node this instance holds. That is why sorting first worked (it navigated away) and
    // sorting after a filter did not: the nodes were dead by the time they were clicked.
    ["input", "change"].forEach(function (type) {
      select.addEventListener(type, function (event) {
        event.stopPropagation();
      });
    });
    this.sort = this.param(SORT_KEY) || select.value || "";
    if (this.sort && select.value !== this.sort) select.value = this.sort;

    select.addEventListener("change", function () {
      self.sort = select.value;
      if (!self.ready) return; // the index is still loading; the URL already holds the state
      if (!self.hasOwnFilter()) {
        // Nothing of ours is filtering: let Shopify do it, server-side. Assigning to
        // `location.search` would drop the fragment, so the whole URL is rebuilt.
        var params = new URLSearchParams(window.location.search);
        params.set("sort_by", select.value);
        window.location.href =
          window.location.pathname + "?" + params.toString() + window.location.hash;
        return;
      }
      self.refilter();
    });
  };

  /* While this facet is filtering, only the orders the catalogue can actually produce stay
   * selectable. Leaving "Best selling" clickable would change nothing when picked, which
   * reads as a broken control rather than an unsupported one. */
  ModelFacet.prototype.updateSortOptions = function () {
    var self = this;
    var select = document.getElementById("collection-sort-order");
    if (!select) return;
    var filtering = this.hasOwnFilter();

    Array.prototype.forEach.call(select.options, function (option) {
      var orderKey = ORDER_SORTS[option.value];
      var supported =
        !filtering ||
        Object.prototype.hasOwnProperty.call(SORTERS, option.value) ||
        Boolean(orderKey && self.orders[orderKey]);
      option.disabled = !supported;
      option.textContent = option.textContent.replace(/ \(unavailable while filtering\)$/, "");
      if (!supported) option.textContent += " (unavailable while filtering)";
    });

    if (filtering && select.selectedOptions[0] && select.selectedOptions[0].disabled) {
      // Only an order the page happened to load with, never one just chosen — the change
      // handler always selects an enabled option.
      this.sort = "";
      select.value = "title-ascending";
    }
  };

  ModelFacet.prototype.finalise = function (indexes) {
    return this.sortResults(this.applyFacets(indexes));
  };

  ModelFacet.prototype.sortResults = function (indexes) {
    var orderKey = ORDER_SORTS[this.sort];
    if (orderKey && this.orders[orderKey]) {
      var rank = {};
      this.orders[orderKey].forEach(function (slot, position) {
        rank[slot] = position;
      });
      // Anything the captured order does not mention goes last rather than to the front,
      // which is what a missing key would otherwise do.
      return indexes.slice().sort(function (a, b) {
        var ra = rank[a] === undefined ? Infinity : rank[a];
        var rb = rank[b] === undefined ? Infinity : rank[b];
        return ra - rb;
      });
    }

    var compare = SORTERS[this.sort];
    if (!compare) return indexes;
    var catalog = this.catalog;
    return indexes.slice().sort(function (a, b) {
      return compare(catalog[a], catalog[b]);
    });
  };

  ModelFacet.prototype.bind = function () {
    this.bindFacet();
    this.takeOverSort();
    this.hookThemeRender();

    var self = this;
    window.addEventListener("popstate", function () {
      self.restore();
    });
  };

  /* `facets.js` replaces the whole sidebar (`renderFilters`) and the whole grid
   * (`renderProductGridContainer`) on every sort or native filter change. The facet
   * markup lives inside the sidebar, so it comes back empty and detached from this
   * instance. Wrapping the two functions is exact; watching the DOM was not, because a
   * theme swap looks identical to this facet's own writes. */
  ModelFacet.prototype.hookThemeRender = function () {
    var self = this;
    if (typeof FacetFiltersForm === "undefined" || FacetFiltersForm.azModelFacetHooked) return;
    FacetFiltersForm.azModelFacetHooked = true;

    ["renderFilters", "renderProductGridContainer"].forEach(function (name) {
      var original = FacetFiltersForm[name];
      if (typeof original !== "function") return;
      FacetFiltersForm[name] = function () {
        var result = original.apply(this, arguments);
        setTimeout(function () {
          var root = document.querySelector("[data-model-facet]");
          if (!root) return;
          self.adopt(root);
          self.bindFacet();
          self.restore();
        }, 0);
        return result;
      };
    });
  };

  ModelFacet.prototype.bindFacet = function () {
    var self = this;

    if (this.availabilityList) {
      this.availabilityList.addEventListener("input", function (event) {
      event.stopPropagation();
    });

    this.availabilityList.addEventListener("click", function (event) {
        var item = event.target.closest(".facet-options-list-item");
        var option = item && item.querySelector("[data-stock-value]");
        if (!option) return;
        var value = option.dataset.stockValue;
        self.guard("Availability", function () {
          self.stock = self.stock === value ? "" : value;
          self.refilterInner();
        });
      });
    }

    if (this.typeList) {
      this.typeList.addEventListener("input", function (event) {
        event.stopPropagation();
      });

      this.typeList.addEventListener("click", function (event) {
        var item = event.target.closest(".facet-options-list-item");
        var option = item && item.querySelector("[data-type-value]");
        if (!option) return;
        var value = option.dataset.typeValue;
        self.guard("Part Type", function () {
          var at = self.partTypes.indexOf(value);
          if (at === -1) self.partTypes.push(value);
          else self.partTypes.splice(at, 1);
          self.refilterInner();
        });
      });
    }

    if (this.typeMore) {
      this.typeMore.addEventListener("click", function (event) {
        event.preventDefault();
        self.typeVisible += TYPE_STEP;
        self.renderTypes();
      });
    }

    /* AZP-7303 — typing or dragging only moves the other control; nothing reaches the
     * grid until Apply. The ticket asks for exactly that, and it is also what makes a
     * slider usable: filtering on every pixel of a drag would refilter a few hundred
     * times per gesture.
     *
     * `facets.js` listens for `input` on anything inside `.facet` and submits the
     * theme's filter form, which re-renders the section and replaces these controls
     * with empty ones — the typed digits vanish mid-keystroke. Every handler here
     * stops the event before it can reach that listener. */
    [this.priceFrom, this.priceTo].forEach(function (input) {
      if (!input) return;
      input.addEventListener("input", function (event) {
        event.stopPropagation();
        var from = parseFloat(self.priceFrom && self.priceFrom.value);
        var to = parseFloat(self.priceTo && self.priceTo.value);
        self.minPrice = isNaN(from) ? null : from;
        self.maxPrice = isNaN(to) ? null : to;
        self.syncPriceSlider(input === self.priceFrom ? "min" : "max");
      });
    });

    [this.priceMinRange, this.priceMaxRange].forEach(function (input) {
      if (!input) return;
      input.addEventListener("input", function (event) {
        event.stopPropagation();
        var which = input === self.priceMinRange ? "min" : "max";
        if (which === "min") self.minPrice = parseFloat(input.value);
        else self.maxPrice = parseFloat(input.value);

        var range = self.syncPriceSlider(which);
        if (!range) return;
        self.minPrice = range.low;
        self.maxPrice = range.high;
        if (self.priceFrom) self.priceFrom.value = range.low;
        if (self.priceTo) self.priceTo.value = range.high;
      });
    });

    if (this.priceChipList) {
      this.priceChipList.addEventListener("click", function (event) {
        if (!event.target.closest("[data-price-clear]")) return;
        event.preventDefault();
        self.guard("Price", function () {
          self.clearPrice();
        });
      });
    }

    if (this.priceApply) {
      this.priceApply.addEventListener("click", function (event) {
        event.preventDefault();
        event.stopPropagation();
        self.guard("Price", function () {
          // A range covering everything is no filter at all; leaving it set would
          // disable the sort orders the server does better, for nothing.
          if (self.minPrice === 0) self.minPrice = null;
          if (self.priceCeiling && self.maxPrice === self.priceCeiling) {
            self.maxPrice = null;
          }
          self.refilterInner();
        });
      });
    }

    this.brandList.addEventListener("input", function (event) {
      event.stopPropagation();
    });

    this.brandList.addEventListener("click", function (event) {
      // AZP-7303 — the x on the chip. Only ever present while this facet is filtering;
      // a brand the page stands for carries a real link instead.
      if (event.target.closest("[data-brand-clear]")) {
        event.preventDefault();
        self.guard("Brand", function () {
          self.clearBrand();
        });
        return;
      }

      var item = event.target.closest(".facet-options-list-item");
      var option = item && item.querySelector("[data-brand-value]");
      if (!option) return;
      var slug = option.dataset.brandValue;
      self.guard("Brand", function () {
        // AZP-7303 — unticking the brand a brand-collection page stands for means
        // leaving that page; there is no filter here to switch off.
        if (!self.brandSlug && slug === self.pageBrand) {
          window.location.href = "/collections/all";
          return;
        }
        if (slug === self.brandSlug) self.clearBrand();
        else self.selectBrand(slug);
      });
    });

    this.brandMore.addEventListener("click", function (event) {
      event.preventDefault();
      self.brandVisible += BRAND_STEP;
      self.renderBrands();
    });

    this.list.addEventListener("input", function (event) {
      event.stopPropagation();
    });

    this.list.addEventListener("click", function (event) {
      var item = event.target.closest(".facet-options-list-item");
      var option = item && item.querySelector("[data-model-value]");
      if (!option) return;
      var model = option.dataset.modelValue;
      self.guard("Model", function () {
        if (model === self.model) self.clearModel();
        else self.selectModel(model);
      });
    });

    /* AZP-7424 — realtime, on `input`: no Enter, no debounce, because filtering an array
     * of strings and repainting at most 20 options is cheaper than the keystroke that
     * triggered it.
     *
     * `stopPropagation` for the same reason the price fields do it: `facets.js` listens
     * for `input` on anything inside `.facet` and submits the theme's filter form, which
     * re-renders the sidebar and replaces this field with an empty one — the typed
     * characters would vanish mid-keystroke. */
    if (this.search) {
      this.search.addEventListener("input", function (event) {
        event.stopPropagation();
        var keyword = self.search.value.trim().toLowerCase();
        if (keyword === self.modelQuery) return;
        self.modelQuery = keyword;
        self.modelVisible = MODEL_STEP;
        // A new keyword is a new list; replacing the rows does not reset the scroll on
        // its own, so a search run after Load more would open part-way down.
        self.list.scrollTop = 0;

        // A keyword searches the whole brand, not only the pages already pulled, so
        // "No results found" is never an artefact of lazy loading. Re-read the field
        // afterwards: the shopper keeps typing while the pages are in flight.
        if (keyword && self.page < self.pages) {
          var epoch = self.modelEpoch;
          self.setStatus("Searching…");
          self.loadAllModels().then(function () {
            if (epoch === self.modelEpoch) self.renderModels();
          });
          return;
        }
        self.renderModels();
      });

      // The field is not inside a form, so Enter submits nothing — but it must not reach
      // the theme's filter listeners either.
      self.search.addEventListener("keydown", function (event) {
        if (event.key === "Enter") event.preventDefault();
        event.stopPropagation();
      });
    }

    this.more.addEventListener("click", function (event) {
      event.preventDefault();
      // Where the batch about to be added starts, read before the window widens.
      var firstNew = self.modelVisible;
      self.modelVisible += MODEL_STEP;
      var reveal = function () {
        self.revealModel(firstNew);
      };
      if (self.models.length <= self.modelVisible && self.page < self.pages) {
        // The next page has to land before there is anything to scroll to.
        self.loadNextPage().then(reveal);
      } else {
        self.renderModels();
        reveal();
      }
    });

    this.clearButton.addEventListener("click", function (event) {
      event.preventDefault();
      self.clearModel();
    });

    // AZP-7303 — unfolding a category list only reveals markup the server already sent;
    // there is nothing to fetch and nothing to filter.
    Array.prototype.forEach.call(
      document.querySelectorAll("[data-category-more]"),
      function (button) {
        button.addEventListener("click", function (event) {
          event.preventDefault();
          var content = button.closest(".facet-content");
          if (!content) return;
          Array.prototype.forEach.call(
            content.querySelectorAll(".az-cat-overflow"),
            function (item) {
              item.classList.remove("az-cat-overflow");
            }
          );
          button.hidden = true;
        });
      }
    );
  };



  document.addEventListener("DOMContentLoaded", function () {
    var root = document.querySelector("[data-model-facet]");
    if (root && root.dataset.indexBase) new ModelFacet(root);
  });
})();
